import { FlexBox } from '@dixa/lib-shared-habla';
import {
  columnOrderingFeature,
  columnPinningFeature,
  columnResizingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createColumnHelper,
  metaHelper,
  tableFeatures,
} from '@tanstack/react-table';
import type { SearchConversationEnriched } from 'lib-search-types';

import { AvatarPlaceholder } from './components/placeholders/avatarPlaceholder/avatarPlaceholder';
import { TextPlaceholder } from './components/placeholders/textPlaceholder/textPlaceholder';
import { ConversationsTablePreferences } from './components/preferences/conversationsTablePreferences';
import { ConversationsTableActions } from './components/rows/conversationRow/components/rowCells/actions/conversationsTableActions';
import { ConversationsTableAgentPreference } from './components/rows/conversationRow/components/rowCells/agentPreference/conversationsTableAgentPreference';
import { ConversationsTableAssignedAgent } from './components/rows/conversationRow/components/rowCells/assignedAgent/conversationsTableAssignedAgent';
import { ConversationsTableChannel } from './components/rows/conversationRow/components/rowCells/channel/conversationsTableChannel';
import { ConversationsTableCsid } from './components/rows/conversationRow/components/rowCells/csid/conversationsTableCsid';
import { ConversationsTableCustomer } from './components/rows/conversationRow/components/rowCells/customer/conversationsTableCustomer';
import { ConversationsTableDirection } from './components/rows/conversationRow/components/rowCells/direction/conversationsTableDirection';
import { ConversationsTableFollowUp } from './components/rows/conversationRow/components/rowCells/followUp/conversationsTableFollowUp';
import { ConversationsTableLanguage } from './components/rows/conversationRow/components/rowCells/language/conversationsTableLanguage';
import { ConversationsTableMessageCount } from './components/rows/conversationRow/components/rowCells/messageCount/conversationsTableMessageCount';
import { ConversationsTableQueue } from './components/rows/conversationRow/components/rowCells/queue/conversationsTableQueue';
import { ConversationsTableQueuedAt } from './components/rows/conversationRow/components/rowCells/queuedAt/conversationsTableQueuedAt';
import { ConversationsTableStatus } from './components/rows/conversationRow/components/rowCells/status/conversationsTableStatus';
import { ConversationsTableTags } from './components/rows/conversationRow/components/rowCells/tags/conversationsTableTags';
import { ConversationsTableTimestamp } from './components/rows/conversationRow/components/rowCells/timestamp/conversationsTableTimestamp';
import {
  type ConversationsTableColumnMeta,
  type ConversationsTableMeta,
} from './conversationsTable.types';

/** TanStack features registered on the conversations table. */
export const conversationsTableFeatures = tableFeatures({
  columnOrderingFeature,
  columnPinningFeature,
  columnResizingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  columnMeta: metaHelper<ConversationsTableColumnMeta>(),
  tableMeta: metaHelper<ConversationsTableMeta>(),
});

export type ConversationsTableFeatures = typeof conversationsTableFeatures;

const columnHelper = createColumnHelper<
  ConversationsTableFeatures,
  SearchConversationEnriched
>();

/** The columns of the conversations table, in their default order. */
export const conversationsTableColumns = columnHelper.columns([
  columnHelper.display({
    id: 'conversation',
    size: 132,
    enableHiding: false,
    header: 'Conversation',
    cell: () => <ConversationsTableCsid />,
    meta: {
      placeholder: {
        header: '74px',
        cell: <TextPlaceholder height="24px" borderRadius="100" />,
      },
      truncate: false,
    },
  }),
  columnHelper.display({
    id: 'customer',
    size: 320,
    enableHiding: false,
    header: 'Customer',
    cell: () => <ConversationsTableCustomer />,
    meta: {
      width: '32%',
      placeholder: {
        header: '60px',
        cell: (
          <FlexBox gap="300" alignItems="center">
            <AvatarPlaceholder />
            <TextPlaceholder width="60%" />
          </FlexBox>
        ),
      },
    },
  }),
  columnHelper.display({
    id: 'queue',
    size: 180,
    header: 'Queue',
    cell: () => <ConversationsTableQueue />,
    meta: {
      width: '13%',
      placeholder: {
        header: '40px',
        cell: <TextPlaceholder width="60%" />,
      },
    },
  }),
  columnHelper.display({
    id: 'status',
    size: 140,
    header: 'Status',
    cell: () => <ConversationsTableStatus />,
    meta: {
      placeholder: {
        header: '40px',
        cell: <TextPlaceholder width="50%" />,
      },
    },
  }),
  columnHelper.display({
    id: 'channel',
    size: 140,
    header: 'Channel',
    cell: () => <ConversationsTableChannel />,
    meta: {
      width: '10%',
      placeholder: {
        header: '50px',
        cell: <TextPlaceholder width="60%" />,
      },
    },
  }),
  columnHelper.display({
    id: 'assignedAgent',
    size: 200,
    header: 'Assigned agent',
    cell: () => <ConversationsTableAssignedAgent />,
    meta: {
      width: '15%',
      placeholder: {
        header: '88px',
        cell: (
          <FlexBox gap="300" alignItems="center">
            <AvatarPlaceholder />
            <TextPlaceholder width="50%" />
          </FlexBox>
        ),
      },
    },
  }),
  columnHelper.display({
    id: 'lastMessage',
    size: 140,
    header: 'Last message',
    cell: () => (
      <ConversationsTableTimestamp timestampKey="last_message_created_at" />
    ),
    meta: {
      width: '10%',
      sortBy: 'last_message_created_at',
      tooltip: 'Based on customer, agent, and automated messages.',
      placeholder: {
        header: '50px',
        cell: <TextPlaceholder width="80%" />,
      },
    },
  }),
  columnHelper.display({
    id: 'created',
    size: 140,
    header: 'Created',
    cell: () => <ConversationsTableTimestamp timestampKey="created_at" />,
    meta: {
      width: '10%',
      sortBy: 'created_at',
      placeholder: {
        header: '50px',
        cell: <TextPlaceholder width="80%" />,
      },
    },
  }),
  columnHelper.display({
    id: 'queued',
    size: 140,
    header: 'Last queued',
    cell: () => <ConversationsTableQueuedAt />,
    meta: {
      sortBy: 'queued_at',
      tooltip: 'When the conversation was last put in a queue.',
      isHiddenByDefault: true,
      placeholder: {
        header: '70px',
        cell: <TextPlaceholder width="80%" />,
      },
    },
  }),
  columnHelper.display({
    id: 'agentPreference',
    size: 200,
    header: 'Reserved for',
    cell: () => <ConversationsTableAgentPreference />,
    meta: {
      tooltip:
        'The agent the conversation is reserved for. Hover the icon to see why.',
      isHiddenByDefault: true,
      placeholder: {
        header: '80px',
        cell: (
          <FlexBox gap="300" alignItems="center">
            <AvatarPlaceholder />
            <TextPlaceholder width="50%" />
          </FlexBox>
        ),
      },
    },
  }),
  columnHelper.display({
    id: 'pendingExpirationAt',
    size: 120,
    header: 'Follow up',
    cell: () => <ConversationsTableFollowUp />,
    meta: {
      sortBy: 'pending_expiration_at',
      isHiddenByDefault: true,
      placeholder: {
        header: '70px',
        cell: <TextPlaceholder width="60%" />,
      },
    },
  }),
  columnHelper.display({
    id: 'direction',
    size: 120,
    header: 'Direction',
    cell: () => <ConversationsTableDirection />,
    meta: {
      isHiddenByDefault: true,
      placeholder: {
        header: '60px',
        cell: <TextPlaceholder width="60%" />,
      },
    },
  }),
  columnHelper.display({
    id: 'language',
    size: 120,
    header: 'Language',
    cell: () => <ConversationsTableLanguage />,
    meta: {
      sortBy: 'language',
      tooltip:
        'Detected automatically. If it’s wrong, you can change it in the conversation.',
      isHiddenByDefault: true,
      placeholder: {
        header: '60px',
        cell: <TextPlaceholder width="60%" />,
      },
    },
  }),
  columnHelper.display({
    id: 'tags',
    size: 200,
    header: 'Tags',
    cell: () => <ConversationsTableTags />,
    meta: {
      isHiddenByDefault: true,
      placeholder: {
        header: '40px',
        cell: <TextPlaceholder width="60%" />,
      },
    },
  }),
  columnHelper.display({
    id: 'inboundMessageCount',
    size: 120,
    header: 'Received',
    cell: () => (
      <ConversationsTableMessageCount countKey="inbound_message_count" />
    ),
    meta: {
      sortBy: 'inbound_message_count',
      tooltip: 'Messages received from the customer.',
      isHiddenByDefault: true,
      placeholder: {
        header: '60px',
        cell: <TextPlaceholder width="40%" />,
      },
    },
  }),
  columnHelper.display({
    id: 'outboundMessageCount',
    size: 120,
    header: 'Sent',
    cell: () => (
      <ConversationsTableMessageCount countKey="non_automated_outbound_message_count" />
    ),
    meta: {
      sortBy: 'non_automated_outbound_message_count',
      tooltip: 'Messages sent to the customer, not counting automated ones.',
      isHiddenByDefault: true,
      placeholder: {
        header: '40px',
        cell: <TextPlaceholder width="40%" />,
      },
    },
  }),
  columnHelper.display({
    id: 'actions',
    size: 96,
    enableHiding: false,
    enableResizing: false,
    header: ({ table }) => <ConversationsTablePreferences table={table} />,
    cell: () => <ConversationsTableActions />,
    meta: {
      placeholder: {
        header: '24px',
        cell: (
          <FlexBox justifyContent="flex-end" gap="200" padding="100">
            <TextPlaceholder width="24px" />
            <TextPlaceholder width="24px" />
          </FlexBox>
        ),
      },
    },
  }),
]);
